import React, { useState, useEffect, useRef } from "react";
import { Star, Quote, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { reviewsAPI, contentAPI } from "../services/api";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const GoogleReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionData, setSectionData] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsData, metaData, sectionsData] = await Promise.allSettled([
          reviewsAPI.getVisible(1),
          reviewsAPI.getMeta(),
          contentAPI.getSections("home").catch(() => []),
        ]);
        if (reviewsData.status === "fulfilled") {
          setReviews(reviewsData.value);
        }
        if (metaData.status === "fulfilled" && metaData.value) {
          setMeta(metaData.value);
        }
        if (sectionsData.status === "fulfilled") {
          const reviewSection = sectionsData.value.find(s => s.section_type === "google_reviews");
          if (reviewSection?.content_data) {
            setSectionData(reviewSection.content_data);
          }
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Listen for admin content updates
    const handleUpdate = () => {
      contentAPI.getSections("home").then(sections => {
        const reviewSection = sections.find(s => s.section_type === "google_reviews");
        if (reviewSection?.content_data) {
          setSectionData(reviewSection.content_data);
        }
      }).catch(() => {});
    };
    window.addEventListener('contentUpdated', handleUpdate);
    return () => window.removeEventListener('contentUpdated', handleUpdate);
  }, []);

  if (!loading && reviews.length === 0) return null;

  // CMS settings with defaults
  const cfg = sectionData || {};
  const title = cfg.title || "Das sagen unsere Kunden";
  const titleHighlight = cfg.title_highlight || "Kunden";
  const description = cfg.description || "Echte Bewertungen von zufriedenen Kunden und Angehoerigen auf Google";
  const layout = cfg.layout || "grid";
  const titleColor = cfg.title_color || "";
  const titleFontSize = cfg.title_font_size || "";
  const titleFontFamily = cfg.title_font_family || "";
  const highlightColor = cfg.highlight_color || "";
  const descColor = cfg.description_color || "";
  const descFontSize = cfg.description_font_size || "";
  const descFontFamily = cfg.description_font_family || "";
  const authorNameColor = cfg.author_name_color || "";
  const reviewTextColor = cfg.review_text_color || "";
  const starColor = cfg.star_color || "#facc15";
  const cardBgColor = cfg.card_bg_color || "";
  const showOverallRating = cfg.show_overall_rating !== false;
  const showGoogleLink = cfg.show_google_link !== false;
  const minRating = cfg.min_rating || 1;

  // Filter reviews by min rating from CMS
  const filteredReviews = reviews.filter(r => r.rating >= minRating);

  // Render title with optional highlight word
  const renderTitle = () => {
    if (!titleHighlight || !title.includes(titleHighlight)) {
      return <span>{title}</span>;
    }
    const parts = title.split(titleHighlight);
    return (
      <>
        {parts[0]}
        <span
          className={highlightColor ? "" : "gradient-text"}
          style={highlightColor ? { color: highlightColor } : {}}
        >
          {titleHighlight}
        </span>
        {parts[1] || ""}
      </>
    );
  };

  const renderStars = (rating) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${star <= rating ? "fill-current" : "text-gray-500"}`}
          style={star <= rating ? { color: starColor } : {}}
        />
      ))}
    </div>
  );

  const googleMapsUrl = "https://www.google.com/maps/place/?q=place_id:ChIJFfNlD1PLuEcRHyN40K4L1EM";

  // Review card component
  const ReviewCard = ({ review, isListLayout = false }) => {
    const cardClasses = cardBgColor ? "" : "glass-effect";
    return (
      <div
        className={`${cardClasses} ${isListLayout ? "flex gap-5 sm:gap-6" : ""} p-5 sm:p-6 rounded-2xl border border-[var(--vortex-border)] hover-lift transition-all duration-300 flex ${isListLayout ? "flex-row items-start" : "flex-col"}`}
        style={cardBgColor ? { backgroundColor: cardBgColor } : {}}
      >
        <div className={`flex items-start gap-3 ${isListLayout ? "flex-shrink-0" : "mb-4"}`}>
          {review.profile_photo_url ? (
            <img
              src={review.profile_photo_url}
              alt={review.author_name}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--vortex-accent-primary)] to-[var(--vortex-accent-secondary)] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">
                {review.author_name?.charAt(0)?.toUpperCase() || "?"}
              </span>
            </div>
          )}
          {!isListLayout && (
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold truncate" style={{ color: authorNameColor || "#ffffff" }}>{review.author_name}</h4>
              <div className="flex items-center gap-2 mt-0.5">
                {renderStars(review.rating)}
                {review.relative_time_description && (
                  <span className="text-xs text-[var(--vortex-text-secondary)]">
                    {review.relative_time_description}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={`${isListLayout ? "flex-1 min-w-0" : "flex-1"}`}>
          {isListLayout && (
            <div className="mb-2">
              <h4 className="text-sm font-semibold" style={{ color: authorNameColor || "#ffffff" }}>{review.author_name}</h4>
              <div className="flex items-center gap-2 mt-0.5">
                {renderStars(review.rating)}
                {review.relative_time_description && (
                  <span className="text-xs text-[var(--vortex-text-secondary)]">
                    {review.relative_time_description}
                  </span>
                )}
              </div>
            </div>
          )}
          {review.text && (
            <div>
              <Quote className="w-4 h-4 text-[var(--vortex-accent-primary)]/40 mb-1 flex-shrink-0" />
              <p
                className={`text-sm leading-relaxed ${isListLayout ? "" : "line-clamp-5"}`}
                style={{ color: reviewTextColor || "var(--vortex-text-secondary)" }}
              >
                {review.text}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Carousel navigation
  const getItemsPerView = () => {
    if (typeof window === "undefined") return 3;
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  };
  const carouselItemsPerView = getItemsPerView();
  const carouselMaxIndex = Math.max(0, filteredReviews.length - carouselItemsPerView);

  const scrollCarousel = (direction) => {
    setCarouselIndex(prev => {
      if (direction === "next") return Math.min(prev + 1, carouselMaxIndex);
      return Math.max(prev - 1, 0);
    });
  };

  // Layout renderers
  const renderGridLayout = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
      {filteredReviews.map((review) => (
        <ReviewCard key={review.id} review={review} />
      ))}
    </div>
  );

  const renderListLayout = () => (
    <div className="flex flex-col gap-4 sm:gap-5 max-w-4xl mx-auto">
      {filteredReviews.map((review) => (
        <ReviewCard key={review.id} review={review} isListLayout />
      ))}
    </div>
  );

  const renderCarouselLayout = () => (
    <div className="relative">
      <div className="overflow-hidden" ref={carouselRef}>
        <div
          className="flex gap-5 sm:gap-6 transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${carouselIndex * (100 / carouselItemsPerView)}%)` }}
        >
          {filteredReviews.map((review) => (
            <div
              key={review.id}
              className="flex-shrink-0"
              style={{
                width: `calc(${100 / carouselItemsPerView}% - ${(carouselItemsPerView - 1) * 20 / carouselItemsPerView}px)`,
              }}
            >
              <ReviewCard review={review} />
            </div>
          ))}
        </div>
      </div>
      {filteredReviews.length > carouselItemsPerView && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => scrollCarousel("prev")}
            disabled={carouselIndex === 0}
            className="w-10 h-10 rounded-full border border-[var(--vortex-border)] flex items-center justify-center text-[var(--vortex-text-secondary)] hover:text-white hover:border-[var(--vortex-accent-primary)] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex gap-1.5">
            {Array.from({ length: carouselMaxIndex + 1 }, (_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all ${i === carouselIndex ? "bg-[var(--vortex-accent-primary)] w-6" : "bg-[var(--vortex-border)] w-2"}`}
              />
            ))}
          </div>
          <button
            onClick={() => scrollCarousel("next")}
            disabled={carouselIndex >= carouselMaxIndex}
            className="w-10 h-10 rounded-full border border-[var(--vortex-border)] flex items-center justify-center text-[var(--vortex-text-secondary)] hover:text-white hover:border-[var(--vortex-accent-primary)] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );

  const renderMasonryLayout = () => {
    const getCols = () => {
      if (typeof window === "undefined") return 3;
      if (window.innerWidth >= 1024) return 3;
      if (window.innerWidth >= 768) return 2;
      return 1;
    };
    const cols = getCols();
    const columns = Array.from({ length: cols }, () => []);
    filteredReviews.forEach((review, i) => {
      columns[i % cols].push(review);
    });

    return (
      <div className="flex gap-5 sm:gap-6">
        {columns.map((column, colIndex) => (
          <div key={colIndex} className="flex-1 flex flex-col gap-5 sm:gap-6">
            {column.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderReviews = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="glass-effect p-5 sm:p-6 rounded-2xl border border-[var(--vortex-border)] animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[var(--vortex-surface)] rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-[var(--vortex-surface)] rounded w-24 mb-2"></div>
                  <div className="h-3 bg-[var(--vortex-surface)] rounded w-16"></div>
                </div>
              </div>
              <div className="h-3 bg-[var(--vortex-surface)] rounded w-full mb-2"></div>
              <div className="h-3 bg-[var(--vortex-surface)] rounded w-3/4"></div>
            </div>
          ))}
        </div>
      );
    }

    if (filteredReviews.length === 0) return null;

    switch (layout) {
      case "list": return renderListLayout();
      case "carousel": return renderCarouselLayout();
      case "masonry": return renderMasonryLayout();
      default: return renderGridLayout();
    }
  };

  return (
    <section id="google-reviews" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-[var(--vortex-accent-primary)]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[var(--vortex-accent-secondary)]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-400/10 border border-yellow-400/20 mb-4 sm:mb-6">
            <GoogleIcon />
            <span className="text-sm font-medium text-yellow-400/90">Google Reviews</span>
          </div>

          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4"
            style={{
              color: titleColor || "white",
              fontSize: titleFontSize || undefined,
              fontFamily: titleFontFamily || undefined,
            }}
          >
            {renderTitle()}
          </h2>
          <p
            className="text-sm sm:text-base max-w-2xl mx-auto"
            style={{
              color: descColor || "var(--vortex-text-secondary)",
              fontSize: descFontSize || undefined,
              fontFamily: descFontFamily || undefined,
            }}
          >
            {description}
          </p>

          {showOverallRating && meta && meta.overall_rating && (
            <div className="flex items-center justify-center gap-3 mt-5 sm:mt-6">
              <div className="flex items-center gap-1.5">
                <span
                  className="text-3xl sm:text-4xl font-bold"
                  style={{ color: titleColor || "white" }}
                >
                  {Number(meta.overall_rating).toFixed(1)}
                </span>
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 sm:w-5 sm:h-5 ${star <= Math.round(meta.overall_rating) ? "fill-current" : "text-gray-500"}`}
                        style={star <= Math.round(meta.overall_rating) ? { color: starColor } : {}}
                      />
                    ))}
                  </div>
                  {meta.total_ratings && (
                    <span className="text-xs text-[var(--vortex-text-secondary)]">
                      {meta.total_ratings} Bewertungen
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Reviews in selected layout */}
        {renderReviews()}

        {/* Link to Google */}
        {showGoogleLink && !loading && filteredReviews.length > 0 && (
          <div className="text-center mt-8 sm:mt-10">
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[var(--vortex-border)] text-sm text-[var(--vortex-text-secondary)] hover:text-white hover:border-[var(--vortex-accent-primary)]/50 transition-all duration-300"
            >
              <GoogleIcon />
              Alle Bewertungen auf Google ansehen
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default GoogleReviews;
